import React, { CSSProperties, useState } from 'react';
import { Dropdown, Modal } from 'antd';
import { useLocation } from 'react-router-dom';
import { BUILD_INFO } from '../buildInfo';
import {
  COLOR_SPECING,
  COLOR_PLANNING,
  COLOR_READY4CODING,
  COLOR_DONE,
  COLOR_REMOVED,
  COLOR_NA,
  COLOR_UNKNOWN,
  COLOR_ITERATION,
  COLOR_CURRENT_ITERATION,
  COLOR_QUARTALS,
  COLOR_DIM,
  PRIORITY_DRIVERS_ALL,
  PRIORITY_DRIVERS_NONE,
  COLOR_NAV_ACTIVE,
} from '../Common/Constants';

interface MenuProps {
  userName: string;
  useAdvancedMode: boolean;
  setUseAdvancedMode: (mode: boolean) => void;
  onLogout: () => void;
  priorityDriverOptions: string[];
  selectedPriorityDriver: string;
  setSelectedPriorityDriver: (value: string) => void;
}

const Menu: React.FC<MenuProps> = (props) => {
  const [showAboutModal, setShowAboutModal] = useState(false);
  const location = useLocation();

  const priorityDrivers = props.priorityDriverOptions.map((option: string) => ({
    value: option,
    label: option,
  }));
  priorityDrivers.unshift({ value: PRIORITY_DRIVERS_ALL, label: PRIORITY_DRIVERS_ALL });
  priorityDrivers.unshift({ value: PRIORITY_DRIVERS_NONE, label: PRIORITY_DRIVERS_NONE });

  const legendItems = [
    { color: COLOR_SPECING, label: "Specing (Not Started / FuncSpec in-progress)" },
    { color: COLOR_PLANNING, label: "Planning (FuncSpec Complete / DevPlan in-progress)" },
    { color: COLOR_READY4CODING, label: "Ready for Coding" },
    { color: COLOR_DONE, label: "Done" },
    { color: COLOR_REMOVED, label: "Removed" },
    { color: COLOR_NA, label: "N/A" },
    { color: COLOR_UNKNOWN, label: "Unknown" },
    { color: COLOR_ITERATION, label: "Iteration Header" },
    { color: COLOR_CURRENT_ITERATION, label: "Current Iteration Header" },
    { color: COLOR_QUARTALS, label: "Quarter Header" },
    { color: COLOR_DIM, label: "Dimmed (Filtered)" },
  ];

  const userMenuItems = [
    {
      key: "AdvancedMode",
      label: props.useAdvancedMode ? "Use Standard mode" : "Use Advanced mode",
    },
    ...(props.useAdvancedMode ? [
      {
        type: 'divider' as const,
      },
      {
        key: "priorityDriver",
        label: "Filter by Priority Driver",
        children: [
          {
            key: "priorityDriver-clear",
            label: props.selectedPriorityDriver ? "Clear filter" : "No filter",
          },
          {
            type: 'divider' as const,
          },
          ...priorityDrivers.map((driver) => ({
            key: `priorityDriver-${driver.value}`,
            label: driver.label,
          })),
        ],
      },
      {
        type: 'divider' as const,
      },
    ] : []),
    {
      key: "about",
      label: "About",
    },
    {
      key: "logout",
      label: "Logout",
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === "logout") {
      props.onLogout();
    } else if (key === "AdvancedMode") {
      props.setUseAdvancedMode(!props.useAdvancedMode);
    } else if (key === "about") {
      setShowAboutModal(true);
    } else if (key === "priorityDriver-clear") {
      props.setSelectedPriorityDriver("");
    } else if (key.startsWith("priorityDriver-")) {
      const value = key.replace("priorityDriver-", "");
      props.setSelectedPriorityDriver(value);
    }
  };

  return (
    <nav>
      <ul style={styles.menu}>
        <li style={styles.menuItem}>
          <a 
            href="/team-planning" 
            style={{
              ...styles.linkBase,
              ...(location.pathname === '/team-planning' && {
                fontWeight: 'bold',
                backgroundColor: COLOR_NAV_ACTIVE,
                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.4)',
              })
            }}
          >
            Team planning
          </a>
        </li>
        <li style={styles.menuItem}>
          <a 
            href="/rnd-planning" 
            style={{
              ...styles.linkBase,
              ...(location.pathname === '/rnd-planning' && {
                fontWeight: 'bold',
                backgroundColor: COLOR_NAV_ACTIVE,
                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.4)',
              })
            }}
          >
            R&D planning
          </a>
        </li>
        {props.useAdvancedMode && (
          <li style={styles.menuItem}>
            <a 
              href="/time-administration" 
              style={{
                ...styles.linkBase,
                ...(location.pathname === '/time-administration' && {
                  fontWeight: 'bold',
                  backgroundColor: COLOR_NAV_ACTIVE,
                  boxShadow: '0 2px 8px rgba(24, 144, 255, 0.4)',
                })
              }}
            >
              Time administration
            </a>
          </li>
        )}
        {/* <li style={styles.menuItem}><a href="/triage" style={styles.link}>Triage</a></li>
        <li style={styles.menuItem}><a href="/time-tracking" style={styles.link}>Time tracking</a></li>
        <li style={styles.menuItem}><a href="/work-in-progress" style={styles.link}>Work in Progress</a></li> */}
        <Dropdown 
          menu={{ items: userMenuItems, onClick: handleMenuClick }} 
          trigger={["click"]}
        >
          <span style={{ color: '#fff', marginLeft: 'auto', fontSize: '16px', cursor: 'pointer' }}>{props.userName}</span>
        </Dropdown>
      </ul>
      <Modal
        title="About Timeline2"
        open={showAboutModal}
        onCancel={() => setShowAboutModal(false)}
        footer={null}
        width={600}
      >
        <div style={styles.aboutContent}>
          <div style={styles.appInfoSection}>
            <p><strong>Application:</strong> Timeline 2.0</p>
            <p><strong>Version:</strong> 0.1.0</p>
            <p><strong>Build Number:</strong> {BUILD_INFO.buildNumber}</p>
            <p><strong>Build Date:</strong> {BUILD_INFO.buildDate}</p>
            <p><strong>Git Branch:</strong> {BUILD_INFO.gitBranch}</p>
            <p><strong>Commit Hash:</strong> {BUILD_INFO.gitCommitHash}</p>
          </div>
          
          <div style={styles.legendSection}>
            <h3 style={styles.legendTitle}>Legend</h3>
            <div style={styles.legendGrid}>
              {legendItems.map((item) => (
                <div key={item.label} style={{...styles.colorBox, backgroundColor: item.color}}>
                  <span style={styles.legendLabel}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </nav>
  );
};

const styles: { [key: string]: CSSProperties } = {
    menu: {
        listStyleType: 'none',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        padding: '10px',
        margin: 0,
        backgroundColor: '#333',
    },
    menuItem: {
        margin: '0 10px',
    },
    linkBase: {
        color: '#fff',
        textDecoration: 'none',
        fontSize: '16px',
        padding: '8px 16px',
        borderRadius: '4px',
        transition: 'background-color 0.2s',
    },
    aboutContent: {
        padding: '10px 0',
    },
    appInfoSection: {
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '1px solid #f0f0f0',
    },
    legendSection: {
        marginTop: '16px',
    },
    legendTitle: {
        margin: '0 0 16px 0',
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#262626',
    },
    legendGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '12px',
    },
    colorBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px',
        borderRadius: '6px',
        border: '1px solid #d9d9d9',
        minHeight: '40px',
        textAlign: 'center' as const,
    },
    legendLabel: {
        fontSize: '14px',
        color: '#ffffff',
        lineHeight: '1.4',
        fontWeight: 'bold' as const,
        textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
    },
};

export default Menu;
